import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { CheckCircle, Loader2, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeatureCommentForm = ({ feature, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    comment: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/features/${feature.id}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit comment');
      }

      setIsSuccess(true);
      setFormData({ name: '', comment: '' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setIsSuccess(false);
      setError('');
    }, 200);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {isSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <DialogTitle className="text-2xl font-bold text-[#1F1E1E] mb-2">
              Comment Submitted!
            </DialogTitle>
            <DialogDescription className="text-[#3B3535]/70">
              Thank you for your feedback on "{feature.name}". We'll review it soon.
            </DialogDescription>
            <Button
              onClick={handleClose}
              data-testid="comment-close-button"
              className="mt-6 bg-[#B5541B] hover:bg-[#8B3F14] text-white rounded-full"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1F1E1E] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#B5541B]" />
                Comment on Feature
              </DialogTitle>
              <DialogDescription className="text-[#3B3535]/70">
                Share your thoughts on "<span className="font-medium">{feature.name}</span>"
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="commenter_name" className="text-[#3B3535] font-medium">
                  Your Name <span className="text-[#B5541B]">*</span>
                </Label>
                <Input
                  id="commenter_name"
                  data-testid="comment-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
                />
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label htmlFor="comment_text" className="text-[#3B3535] font-medium">
                  Your Comment <span className="text-[#B5541B]">*</span>
                </Label>
                <Textarea
                  id="comment_text"
                  data-testid="comment-text-input"
                  required
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder="Share your thoughts, suggestions, or use cases for this feature..."
                  rows={4}
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] resize-none"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="comment-submit-button"
                disabled={isSubmitting || !formData.name || !formData.comment}
                className="w-full bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold py-5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Comment'
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeatureCommentForm;
